import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { API_URL } from "../../constant";
import {
  MdOutlineAccountBalanceWallet,
  MdRefresh,
  MdCheckCircle,
  MdArrowBack,
  MdShoppingBag,
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
} from "react-icons/md";
import { FaCoins } from "react-icons/fa";
import "./RetailTopCoinPurchase.css";
import { formatCashValue } from "./RetailTopCoinPurchase";

export default function RetailTopCoinPurchaseHistory() {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [fromIndex, setFromIndex] = useState(0);
  const [toIndex, setToIndex] = useState(0);
  const [summaryData, setSummaryData] = useState(null);

  // 1. Fetch live balance from rate endpoint
  const fetchBalance = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/retail-business/topcoin-purchase/rate`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.current_balance !== undefined) {
          setCurrentBalance(data.current_balance);
        }
      }
    } catch (err) {
      console.error("Failed to load balance:", err);
    }
  };

  // 2. Fetch past TopCoin purchase history with pagination support
  const fetchPurchaseHistory = async (page = 1, limit = perPage) => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;

    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/retail-business/topcoin-purchase/history?page=${page}&per_page=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const records = result.data?.data || result.data || [];
        setHistory(Array.isArray(records) ? records : []);

        if (result.summary) {
          setSummaryData(result.summary);
        }

        if (result.data?.current_page !== undefined) {
          setCurrentPage(result.data.current_page);
          setTotalPages(result.data.last_page || 1);
          setTotalRecords(result.data.total !== undefined ? result.data.total : records.length);
          setFromIndex(
            result.data.from !== undefined
              ? result.data.from
              : records.length > 0
              ? (result.data.current_page - 1) * limit + 1
              : 0
          );
          setToIndex(
            result.data.to !== undefined
              ? result.data.to
              : records.length > 0
              ? (result.data.current_page - 1) * limit + records.length
              : 0
          );
        } else {
          setCurrentPage(1);
          setTotalPages(1);
          setTotalRecords(Array.isArray(records) ? records.length : 0);
          setFromIndex(records.length > 0 ? 1 : 0);
          setToIndex(records.length);
        }
      }
    } catch (err) {
      console.error("Failed to fetch purchase history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/retail-agent-login");
      return;
    }
    fetchBalance();
    fetchPurchaseHistory(1, perPage);
  }, [navigate]);

  const handlePerPageChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setPerPage(newLimit);
    setCurrentPage(1);
    fetchPurchaseHistory(1, newLimit);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !historyLoading) {
      setCurrentPage(newPage);
      fetchPurchaseHistory(newPage, perPage);
    }
  };

  // Generate pagination buttons with smart ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push("...");
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  // Date and Time matching ManualTopCoinWindow & RetailTcDistribution
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const shopName =
    userData?.retail_name ||
    userData?.shop_details?.retail_name ||
    `${userData?.first_name || "Retail"} ${userData?.last_name || "Business"}`.trim();

  // Summary statistics (use backend lifetime summary if available, otherwise current slice)
  const completedPurchases = history.filter((p) => p.status === "completed");
  const totalCoinsPurchased = summaryData
    ? summaryData.total_coins_bought
    : completedPurchases.reduce(
        (acc, curr) => acc + (Number(curr.quantity) || 0),
        0
      );
  const totalAmountSpent = summaryData
    ? summaryData.total_paid
    : completedPurchases.reduce(
        (acc, curr) => acc + (Number(curr.total_payable) || 0),
        0
      );
  const completedOrdersCount = summaryData
    ? summaryData.completed_orders_count
    : completedPurchases.length;

  return (
    <div className="tcp-outer tcp-history-outer tcp-outer-1 tcp-history-outer-1">
      {/* Dark Plum Decorative Border Frame matching ManualTopCoinWindow & RetailTcDistribution */}
      <div className="tcp-border-frame">
        {/* White Receipt Card */}
        <div className="tcp-card">
          {/* Header */}
          <div className="tcp-header">
            <div className="tcp-header-left">
              <h3>
                TopCoin UK<br />(TCUK)
              </h3>
            </div>
            <div className="tcp-header-right">
              {displayDate} {displayTime}
            </div>
          </div>

          {/* Branding Row */}
          <div className="tcp-branding">
            <p className="brand-name">TopCoinLand</p>
            <a
              className="brand-link"
              href="https://www.topcoinland.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.topcoinland.com
            </a>
            <div className="tcp-page-badge">
              <FaCoins /> TopCoin Purchase History
            </div>
          </div>

          {/* Account & Available Balance Bar */}
          <div className="tcp-account-bar">
            <div className="tcp-account-info-item">
              <span className="tcp-account-info-label">Retail Account: </span>
              <span className="store-name">{shopName}</span>
            </div>
            <div className="tcp-account-info-item">
              <span className="tcp-account-info-label">Current Balance: </span>
              <span className="wallet-balance">
                {Number(currentBalance).toLocaleString()} TC
              </span>
            </div>
          </div>

          {/* Navigation & Controls Row */}
          <div className="tcp-history-ctrl-bar">
            <button
              type="button"
              className="tcp-action-link-btn primary"
              onClick={() => navigate("/retail-business/topcoin-purchase")}
            >
              <MdArrowBack size={16} />
              <span>Buy More TopCoins</span>
            </button>

            <div className="tcp-history-ctrl-group">
              <button
                type="button"
                className="tcp-action-link-btn"
                onClick={() => {
                  fetchPurchaseHistory(currentPage, perPage);
                  fetchBalance();
                }}
                disabled={historyLoading}
              >
                <MdRefresh size={16} />
                <span>Refresh</span>
              </button>

              <button
                type="button"
                className="tcp-action-link-btn"
                onClick={() => navigate("/retail-business/wallet")}
              >
                <MdOutlineAccountBalanceWallet size={16} />
                <span>Wallets</span>
              </button>
            </div>
          </div>

          {/* Summary Metric Cards */}
          <div className="tcp-summary-row">
            <div className="tcp-summary-box">
              <span className="tcp-summary-label">Total Coins Bought</span>
              <span className="tcp-summary-val" style={{ color: "#701a75" }}>
                {Number(totalCoinsPurchased || 0).toLocaleString()} TC
              </span>
            </div>
            <div className="tcp-summary-box">
              <span className="tcp-summary-label">Total Paid (inc. VAT)</span>
              <span className="tcp-summary-val" style={{ color: "#047857" }}>
                £{Number(totalAmountSpent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="tcp-summary-box">
              <span className="tcp-summary-label">Completed Orders</span>
              <span className="tcp-summary-val">
                {completedOrdersCount}
              </span>
            </div>
          </div>

          {/* Mobile Swipe Hint */}
          <div className="tcp-table-scroll-hint">
            <span>← Swipe table horizontally to see all columns →</span>
          </div>

          {/* Table */}
          <div className="tcp-table-wrapper">
            <table className="tcp-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  {/* <th>Order Ref</th> */}
                  <th>Quantity</th>
                  <th>Unit Cost</th>
                  <th>Total Value</th>
                  <th>VAT (20%)</th>
                  <th>Total Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        textAlign: "center",
                        padding: "28px",
                        color: "#6b7280",
                      }}
                    >
                      <span
                        className="tcp-spinner-sm"
                        style={{ borderTopColor: "#701a75", marginRight: "8px" }}
                      />
                      Loading purchase history...
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        textAlign: "center",
                        padding: "32px",
                        color: "#6b7280",
                      }}
                    >
                      <MdShoppingBag
                        size={32}
                        style={{ color: "#9ca3af", display: "block", margin: "0 auto 8px" }}
                      />
                      No purchase records found yet.
                      <div style={{ marginTop: "8px" }}>
                        <button
                          type="button"
                          className="tcp-action-link-btn primary"
                          onClick={() => navigate("/retail-business/topcoin-purchase")}
                          style={{ margin: "0 auto" }}
                        >
                          Buy TopCoins Now
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
                      </td>
                      {/* <td style={{ fontWeight: 700, fontFamily: "monospace" }}>
                        #{item.id}
                      </td> */}
                      <td style={{ fontWeight: 800, color: "#701a75", whiteSpace: "nowrap" }}>
                        {Number(item.quantity).toLocaleString()} TC
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        £{formatCashValue(item.unit_cost)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        £{Number(item.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        £{Number(item.vat_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                        £{Number(item.total_payable || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span
                          className={`tcp-badge ${
                            item.status === "completed"
                              ? "tcp-badge-success"
                              : item.status === "pending"
                              ? "tcp-badge-pending"
                              : "tcp-badge-failed"
                          }`}
                        >
                          {item.status === "completed" && <MdCheckCircle size={13} />}
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!historyLoading && (
            <div className="tcp-pagination-bar">
              
              {totalPages > 1 && (
                <div className="tcp-pagination-buttons">
                  {/* <button
                    type="button"
                    className="tcp-page-nav-btn tcp-page-nav-icon"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1 || historyLoading}
                    title="First Page"
                    aria-label="First Page"
                  >
                    <MdFirstPage size={18} />
                  </button> */}

                  <button
                    type="button"
                    className="tcp-page-nav-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || historyLoading}
                    title="Previous Page"
                  >
                    <MdChevronLeft size={16} />
                    <span className="tcp-page-btn-text">Prev</span>
                  </button>

                  <div className="tcp-page-numbers-group">
                    {getPageNumbers().map((p, idx) =>
                      p === "..." ? (
                        <span key={`dots-${idx}`} className="tcp-page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={`page-${p}`}
                          type="button"
                          className={`tcp-page-num-btn ${
                            currentPage === p ? "active" : ""
                          }`}
                          onClick={() => handlePageChange(p)}
                          disabled={historyLoading}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    type="button"
                    className="tcp-page-nav-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || historyLoading}
                    title="Next Page"
                  >
                    <span className="tcp-page-btn-text">Next</span>
                    <MdChevronRight size={16} />
                  </button>

                  {/* <button
                    type="button"
                    className="tcp-page-nav-btn tcp-page-nav-icon"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages || historyLoading}
                    title="Last Page"
                    aria-label="Last Page"
                  >
                    <MdLastPage size={18} />
                  </button> */}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
